import {forwardRef, ComponentType} from 'react'
import {createPortal} from 'react-dom'
import {
  Modal as GWCModal,
  ModalProps as GWCModalProps,
  ModalBodyProps as GWCModalBodyProps,
} from '@gwc/core'
import clsx from 'clsx'

import {classes} from './Modal.classes'
import {SidePanel} from './SidePanel'

export type ModalProps = Omit<GWCModalProps<'div'>, 'ref' | 'key'>

type WithPortalArgs<T> =
  | ({
      portalContainer: HTMLDivElement | null | undefined
    } & Omit<T, 'portal'>)
  | ({portalContainer: undefined} & T)

export function withPortal<T extends {portal?: boolean}>(ModalComponent: ComponentType<T>) {
  return (props: WithPortalArgs<T>) => {
    const {portalContainer, ...rest} = props
    if (portalContainer) {
      return createPortal(<ModalComponent portal={false} {...rest} />, portalContainer)
    }

    return <ModalComponent {...rest} />
  }
}

const Modal = Object.assign(
  function Modalv2({
    disableAutoFocus = true,
    enableFocusOutside = true,
    className,
    children,
    onClose,
    backdrop = {},
    ...props
  }: ModalProps) {
    return (
      <GWCModal<'div'>
        {...props}
        backdrop={
          typeof backdrop === 'boolean'
            ? backdrop
            : {
                // @ts-expect-error "не понимается data-testid"
                'data-testid': 'modal.backdrop_click',
                ...backdrop,
                className: clsx(backdrop?.className, classes.backdrop),
              }
        }
        className={clsx(className, classes.modal)}
        disableAutoFocus={disableAutoFocus}
        enableFocusOutside={enableFocusOutside}
        onClose={onClose}
      >
        {children}
      </GWCModal>
    )
  },
  {
    Title: GWCModal.Title,
    Header: GWCModal.Header,
    Body: forwardRef<HTMLDivElement, React.PropsWithChildren<GWCModalBodyProps>>(function (props, ref) {
      return <GWCModal.Body {...props} className={clsx(props.className, classes.modalBody)} ref={ref} />
    }),
    Footer: GWCModal.Footer,
    SidePanel: withPortal(SidePanel),
  },
)

export default Modal
